import React, { useEffect, useRef, useState } from 'react';
import Tooltip from './Tooltip';
import { useResourceUsage } from '../../context/ResourceUsageContext';
import { PlanTier, USAGE_LIMITS } from '../layouts/Header/usageLimits';

const UsageDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { usage } = useResourceUsage();
  const { notesCount, formsCount, chatCount, planName, renewalDate } = usage;
  const limits = USAGE_LIMITS[planName as PlanTier] ?? { notes: 0, forms: 0, aiPrompts: 0 };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  return (
    <div className='relative'>
      <Tooltip text='Usage' placement='bottom-start'>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen((v) => !v)}
          className='rounded-full bg-gray-100 p-2'
          aria-haspopup='true'
          aria-expanded={isOpen}
          aria-label='Usage'
        >
          {/* simple bar-chart icon */}
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='currentColor'
            className='h-5 w-5 text-zinc-800'
          >
            <path d='M3 19.5a.75.75 0 01.75-.75H20.25a.75.75 0 010 1.5H3.75a.75.75 0 01-.75-.75z' />
            <path d='M6 17.25a.75.75 0 01-.75-.75v-6a.75.75 0 011.5 0v6a.75.75 0 01-.75.75zM12 17.25a.75.75 0 01-.75-.75V6a.75.75 0 011.5 0v10.5a.75.75 0 01-.75.75zM18 17.25a.75.75 0 01-.75-.75v-3.75a.75.75 0 011.5 0V16.5a.75.75 0 01-.75.75z' />
          </svg>
        </button>
      </Tooltip>

      <div
        ref={dropdownRef}
        className={`absolute ${isOpen ? '' : 'hidden'} left-0 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-md z-50`}
      >
        <div className='border-b p-3'>
          <span className='mr-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold'
            style={{ background: 'linear-gradient(90deg, #08203E 0%, #557C93 100%)', color: '#fff', borderColor: '#08203E' }}
          >
            {planName}
          </span>
          <span className='text-sm font-medium text-zinc-700'>Usage</span>
        </div>

        <ul className='p-3 space-y-2 text-sm'>
          <li>
            <span className='rounded-lg bg-blue-100 px-2 py-1 font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300'>
              {notesCount}/{String(limits.notes)} Notes
            </span>
          </li>
          <li>
            <span className='rounded-lg bg-green-100 px-2 py-1 font-medium text-green-700 dark:bg-green-900 dark:text-green-300'>
              {formsCount}/{String(limits.forms)} WSIB/MVA Forms
            </span>
          </li>
          <li>
            <span className='rounded-lg bg-purple-100 px-2 py-1 font-medium text-purple-700 dark:bg-purple-900 dark:text-purple-300'>
              {chatCount}/{String(limits.aiPrompts)} AI Chat Prompts
            </span>
          </li>
          <li>
            <span className='rounded-lg bg-orange-100 px-2 py-1 font-medium text-orange-700 dark:bg-orange-900 dark:text-orange-300'>
              Renews on {renewalDate}
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default UsageDropdown;
